// src/api/pagos.js - API para manejo de pagos (adaptado al backend real)
import { apiFetch } from './index.js';

// ==================== FUNCIONES DE UTILIDAD ====================

/**
 * Formatear número con separadores venezolanos
 * @param {number|string} numero - Número a formatear
 * @param {number} decimals - Decimales a mostrar
 * @returns {string} Número formateado
 */
export const formatearNumero = (numero, decimals = 2) => {
  if (numero === null || numero === undefined || numero === '' || isNaN(parseFloat(numero))) {
    return '0,00';
  }
  
  const number = parseFloat(numero);
  return number.toLocaleString('es-VE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Formatear precio con símbolo
 * @param {number} precio - Precio a formatear
 * @param {string} moneda - 'USD' o 'BS'
 * @param {boolean} showSymbol - Mostrar símbolo
 * @returns {string} Precio formateado
 */
export const formatearPrecio = (precio, moneda = 'BS', showSymbol = true) => {
  if (precio === null || precio === undefined || isNaN(parseFloat(precio))) {
    return showSymbol ? (moneda === 'USD' ? '$0,00' : 'Bs 0,00') : '0,00';
  }
  
  const precioNum = parseFloat(precio);
  const formateado = formatearNumero(precioNum, 2);
  
  if (!showSymbol) return formateado;
  
  return moneda === 'USD' ? `$${formateado}` : `Bs ${formateado}`;
};

/**
 * Formatear fecha
 * @param {string} fechaISO - Fecha en formato ISO
 * @param {boolean} completo - Si incluir hora
 * @returns {string} Fecha formateada
 */
export const formatearFecha = (fechaISO, completo = false) => {
  if (!fechaISO) return 'N/A';
  
  try {
    const fecha = new Date(fechaISO);
    const opciones = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    };
    
    if (completo) {
      opciones.hour = '2-digit';
      opciones.minute = '2-digit';
    }
    
    return fecha.toLocaleDateString('es-VE', opciones);
  } catch (error) {
    return fechaISO;
  }
};

/**
 * Convertir estado a texto legible
 * @param {string} estado - Estado del pago
 * @returns {string} Estado formateado
 */
export const convertirEstadoATexto = (estado) => {
  if (!estado) return 'Sin estado';
  return estado.charAt(0).toUpperCase() + estado.slice(1).toLowerCase();
};

/**
 * Obtener color según estado
 * @param {string} estado - Estado del pago
 * @returns {string} Color CSS
 */
export const obtenerColorEstado = (estado) => {
  switch (estado?.toLowerCase()) {
    case 'completado':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'pendiente':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'anulado':
    case 'cancelado':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  }
};

/**
 * Obtener icono según estado
 * @param {string} estado - Estado del pago
 * @returns {string} Nombre del icono
 */
export const obtenerIconoEstado = (estado) => {
  switch (estado?.toLowerCase()) {
    case 'completado':
      return 'mdi:cash-check';
    case 'pendiente':
      return 'mdi:clock-outline';
    case 'anulado':
    case 'cancelado':
      return 'mdi:cancel';
    default:
      return 'mdi:help-circle';
  }
};

/**
 * Obtener icono según método de pago
 * @param {string} metodo - Método de pago
 * @returns {string} Nombre del icono
 */
export const obtenerIconoMetodoPago = (metodo) => {
  if (!metodo) return 'mdi:cash';
  
  const metodoLower = metodo.toLowerCase();
  
  if (metodoLower.includes('efectivo')) return 'mdi:cash';
  if (metodoLower.includes('transferencia')) return 'mdi:bank-transfer';
  if (metodoLower.includes('móvil') || metodoLower.includes('movil')) return 'mdi:cellphone';
  if (metodoLower.includes('débito') || metodoLower.includes('debito')) return 'mdi:credit-card-outline';
  if (metodoLower.includes('crédito') || metodoLower.includes('credito')) return 'mdi:credit-card';
  if (metodoLower.includes('zelle')) return 'mdi:bank-transfer-out';
  if (metodoLower.includes('paypal')) return 'mdi:paypal';
  if (metodoLower.includes('binance')) return 'mdi:currency-btc';
  
  return 'mdi:cash';
};

// ==================== FUNCIONES API PRINCIPALES ====================

/**
 * Obtener todos los pagos
 * @returns {Promise} Lista de pagos
 */
export const obtenerPagos = async () => {
  try {
    console.log('💰 Obteniendo pagos...');
    
    const response = await apiFetch('/Pago');
    
    // La API devuelve directamente el array
    const pagos = response || [];
    
    return {
      success: true,
      data: pagos,
      count: pagos.length,
      message: 'Pagos obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo pagos:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener un pago por ID
 * @param {number} id - ID del pago
 * @returns {Promise} Datos del pago
 */
export const obtenerPagoPorId = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de pago es requerido');
    }
    
    console.log(`🔍 Obteniendo pago ID: ${id}`);
    
    const response = await apiFetch(`/Pago/${id}`);
    
    return {
      success: true,
      data: response,
      message: 'Pago obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo pago ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Crear un nuevo pago - ADAPTADO A TU BACKEND
 * @param {Object} pagoData - Datos del pago
 * @returns {Promise} Pago creado
 */
export const crearPago = async (pagoData) => {
  try {
    // Validar datos requeridos según tu backend
    if (!pagoData.monto || pagoData.monto <= 0) {
      throw new Error('El monto es requerido y debe ser mayor a 0');
    }
    if (!pagoData.moneda) {
      throw new Error('La moneda es requerida (USD o BS)');
    }
    if (!pagoData.idMetodoPago) {
      throw new Error('El método de pago es requerido');
    }
    if (!pagoData.idUsuario) {
      throw new Error('El usuario es requerido');
    }
    
    // Validar que tenga al menos una referencia
    if (!pagoData.idVenta && !pagoData.idCuota && !pagoData.idRecepcionEquipo) {
      throw new Error('El pago debe estar asociado a una venta, cuota o recepción de equipo');
    }
    
    console.log('➕ Creando nuevo pago:', { 
      monto: pagoData.monto,
      moneda: pagoData.moneda,
      idMetodoPago: pagoData.idMetodoPago,
      idVenta: pagoData.idVenta,
      idCuota: pagoData.idCuota,
      idRecepcionEquipo: pagoData.idRecepcionEquipo
    });
    
    // Construir payload EXACTAMENTE como lo espera tu backend
    const pagoPayload = {
      fecha_pago: pagoData.fecha_pago || new Date().toISOString(),
      monto: parseFloat(pagoData.monto),
      moneda: pagoData.moneda,
      referencia: pagoData.referencia || `PAGO-${Date.now()}`,
      descripcion: pagoData.descripcion || '',
      idMetodoPago: parseInt(pagoData.idMetodoPago),
      idUsuario: parseInt(pagoData.idUsuario),
      idVenta: pagoData.idVenta ? parseInt(pagoData.idVenta) : null,
      idCuota: pagoData.idCuota ? parseInt(pagoData.idCuota) : null,
      idRecepcionEquipo: pagoData.idRecepcionEquipo ? parseInt(pagoData.idRecepcionEquipo) : null,
      idFuenteTasa: pagoData.idFuenteTasa ? parseInt(pagoData.idFuenteTasa) : null,
      idTasaVenta: pagoData.idTasaVenta ? parseInt(pagoData.idTasaVenta) : null,
      idTasaCompra: pagoData.idTasaCompra ? parseInt(pagoData.idTasaCompra) : null
    };
    
    console.log('📦 Payload para crear pago:', pagoPayload);
    
    const response = await apiFetch('/Pago', {
      method: 'POST',
      body: JSON.stringify(pagoPayload)
    });
    
    return {
      success: true,
      data: response,
      message: 'Pago creado exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error creando pago:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Actualizar un pago - ADAPTADO A TU BACKEND
 * @param {number} id - ID del pago
 * @param {Object} pagoData - Datos a actualizar
 * @returns {Promise} Pago actualizado
 */
export const actualizarPago = async (id, pagoData) => {
  try {
    if (!id) {
      throw new Error('ID de pago es requerido');
    }
    
    console.log(`✏️ Actualizando pago ID: ${id}`, pagoData);
    
    // Construir payload con los campos que permite actualizar tu backend
    const pagoPayload = {
      idPago: parseInt(id),
      fecha_pago: pagoData.fecha_pago,
      referencia: pagoData.referencia,
      descripcion: pagoData.descripcion,
      idEstadoPago: pagoData.idEstadoPago ? parseInt(pagoData.idEstadoPago) : undefined,
      idMetodoPago: pagoData.idMetodoPago ? parseInt(pagoData.idMetodoPago) : undefined
    };
    
    // Eliminar campos undefined
    Object.keys(pagoPayload).forEach(key => 
      pagoPayload[key] === undefined && delete pagoPayload[key]
    );
    
    const response = await apiFetch(`/Pago/${id}`, {
      method: 'PUT',
      body: JSON.stringify(pagoPayload)
    });
    
    return {
      success: true,
      data: response,
      message: 'Pago actualizado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error actualizando pago ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Verificar si un pago existe
 * @param {number} id - ID del pago
 * @returns {Promise<boolean>} True si existe
 */
export const pagoExists = async (id) => {
  try {
    const response = await apiFetch(`/Pago/${id}/exists`);
    return response === true;
  } catch (error) {
    console.error(`❌ Error verificando pago ${id}:`, error);
    return false;
  }
};

/**
 * Anular un pago
 * @param {number} id - ID del pago
 * @param {Object} anularData - Datos para anular
 * @returns {Promise} Resultado de la anulación
 */
export const anularPago = async (id, anularData) => {
  try {
    if (!id) {
      throw new Error('ID de pago es requerido');
    }
    
    console.log(`🗑️ Anulando pago ID: ${id}`);
    
    const response = await apiFetch(`/Pago/${id}/anular`, {
      method: 'POST',
      body: JSON.stringify({
        idPago: parseInt(id),
        motivo: anularData.motivo || 'Anulación manual',
        reversar_efecto_en_cuota: anularData.reversar_efecto_en_cuota || false,
        idUsuario: anularData.idUsuario
      })
    });
    
    return {
      success: true,
      data: response,
      message: 'Pago anulado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error anulando pago ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

// ==================== BÚSQUEDAS Y FILTROS ====================

/**
 * Buscar pagos con filtros
 * @param {Object} searchParams - Parámetros de búsqueda
 * @returns {Promise} Lista de pagos filtrados
 */
export const buscarPagos = async (searchParams = {}) => {
  try {
    console.log('🔎 Buscando pagos con filtros:', searchParams);
    
    const response = await apiFetch('/Pago/search', {
      method: 'POST',
      body: JSON.stringify(searchParams)
    });
    
    return {
      success: true,
      data: response || [],
      count: response?.length || 0,
      message: 'Búsqueda completada exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error buscando pagos:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener pagos por venta
 * @param {number} idVenta - ID de la venta
 * @returns {Promise} Lista de pagos
 */
export const obtenerPagosPorVenta = async (idVenta) => {
  try {
    if (!idVenta) {
      throw new Error('ID de venta es requerido');
    }
    
    console.log(`💰 Obteniendo pagos por venta ID: ${idVenta}`);
    
    const response = await apiFetch(`/Pago/venta/${idVenta}`);
    
    return {
      success: true,
      data: response || [],
      count: response?.length || 0,
      message: 'Pagos obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo pagos por venta ${idVenta}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener pagos por cuota
 * @param {number} idCuota - ID de la cuota
 * @returns {Promise} Lista de pagos
 */
export const obtenerPagosPorCuota = async (idCuota) => {
  try {
    if (!idCuota) {
      throw new Error('ID de cuota es requerido');
    }
    
    console.log(`💰 Obteniendo pagos por cuota ID: ${idCuota}`);
    
    const response = await apiFetch(`/Pago/cuota/${idCuota}`);
    
    return {
      success: true,
      data: response || [],
      count: response?.length || 0,
      message: 'Pagos obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo pagos por cuota ${idCuota}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener pagos por recepción de equipo
 * @param {number} idRecepcion - ID de la recepción
 * @returns {Promise} Lista de pagos
 */
export const obtenerPagosPorRecepcion = async (idRecepcion) => {
  try {
    if (!idRecepcion) {
      throw new Error('ID de recepción es requerido');
    }
    
    console.log(`💰 Obteniendo pagos por recepción ID: ${idRecepcion}`);
    
    const response = await apiFetch(`/Pago/recepcion/${idRecepcion}`);
    
    return {
      success: true,
      data: response || [],
      count: response?.length || 0,
      message: 'Pagos obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo pagos por recepción ${idRecepcion}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener pagos por cliente
 * @param {number} idCliente - ID del cliente
 * @returns {Promise} Lista de pagos
 */
export const obtenerPagosPorCliente = async (idCliente) => {
  try {
    if (!idCliente) {
      throw new Error('ID de cliente es requerido');
    }
    
    console.log(`👤 Obteniendo pagos por cliente ID: ${idCliente}`);
    
    const response = await apiFetch(`/Pago/cliente/${idCliente}`);
    
    return {
      success: true,
      data: response || [],
      count: response?.length || 0,
      message: 'Pagos obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo pagos por cliente ${idCliente}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener pagos por usuario
 * @param {number} idUsuario - ID del usuario
 * @returns {Promise} Lista de pagos
 */
export const obtenerPagosPorUsuario = async (idUsuario) => {
  try {
    if (!idUsuario) {
      throw new Error('ID de usuario es requerido');
    }
    
    console.log(`👤 Obteniendo pagos por usuario ID: ${idUsuario}`);
    
    const response = await apiFetch(`/Pago/usuario/${idUsuario}`);
    
    return {
      success: true,
      data: response || [],
      count: response?.length || 0,
      message: 'Pagos obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo pagos por usuario ${idUsuario}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener pagos por método de pago
 * @param {number} idMetodoPago - ID del método de pago
 * @returns {Promise} Lista de pagos
 */
export const obtenerPagosPorMetodoPago = async (idMetodoPago) => {
  try {
    if (!idMetodoPago) {
      throw new Error('ID de método de pago es requerido');
    }
    
    console.log(`💳 Obteniendo pagos por método ID: ${idMetodoPago}`);
    
    const response = await apiFetch(`/Pago/metodo/${idMetodoPago}`);
    
    return {
      success: true,
      data: response || [],
      count: response?.length || 0,
      message: 'Pagos obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo pagos por método ${idMetodoPago}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener pagos por estado
 * @param {number} idEstadoPago - ID del estado
 * @returns {Promise} Lista de pagos
 */
export const obtenerPagosPorEstado = async (idEstadoPago) => {
  try {
    if (!idEstadoPago) {
      throw new Error('ID de estado es requerido');
    }
    
    console.log(`📊 Obteniendo pagos por estado ID: ${idEstadoPago}`);
    
    const response = await apiFetch(`/Pago/estado/${idEstadoPago}`);
    
    return {
      success: true,
      data: response || [],
      count: response?.length || 0,
      message: 'Pagos obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo pagos por estado ${idEstadoPago}:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener pagos por rango de fechas
 * @param {string} desde - Fecha desde (YYYY-MM-DD)
 * @param {string} hasta - Fecha hasta (YYYY-MM-DD)
 * @returns {Promise} Lista de pagos
 */
export const obtenerPagosPorRangoFechas = async (desde, hasta) => {
  try {
    if (!desde || !hasta) {
      throw new Error('Fechas desde y hasta son requeridas');
    }
    
    console.log(`📅 Obteniendo pagos del ${desde} al ${hasta}`);
    
    const response = await apiFetch(`/Pago/fechas?desde=${desde}&hasta=${hasta}`);
    
    return {
      success: true,
      data: response || [],
      count: response?.length || 0,
      message: 'Pagos obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo pagos por fechas:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

// ==================== OPERACIONES DE CONVERSIÓN ====================

/**
 * Convertir moneda
 * @param {number} monto - Monto a convertir
 * @param {string} monedaOrigen - Moneda origen (USD o BS)
 * @param {string} monedaDestino - Moneda destino (USD o BS)
 * @param {number} idFuenteTasa - ID de la fuente de tasa
 * @returns {Promise} Resultado de la conversión
 */
export const convertirMoneda = async (monto, monedaOrigen, monedaDestino, idFuenteTasa = null) => {
  try {
    if (!monto || monto <= 0) {
      throw new Error('Monto inválido');
    }
    
    let url = `/Pago/convertir?monto=${monto}&monedaOrigen=${monedaOrigen}&monedaDestino=${monedaDestino}`;
    if (idFuenteTasa) {
      url += `&idFuenteTasa=${idFuenteTasa}`;
    }
    
    const response = await apiFetch(url);
    
    return {
      success: true,
      data: response,
      message: 'Conversión exitosa'
    };
    
  } catch (error) {
    console.error('❌ Error convirtiendo moneda:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener tasa de cambio actual
 * @param {string} monedaOrigen - Moneda origen
 * @param {string} monedaDestino - Moneda destino
 * @param {number} idFuenteTasa - ID de la fuente
 * @returns {Promise} Tasa de cambio
 */
export const obtenerTasaCambio = async (monedaOrigen, monedaDestino, idFuenteTasa = null) => {
  try {
    let url = `/Pago/tasa?monedaOrigen=${monedaOrigen}&monedaDestino=${monedaDestino}`;
    if (idFuenteTasa) {
      url += `&idFuenteTasa=${idFuenteTasa}`;
    }
    
    const response = await apiFetch(url);
    
    return {
      success: true,
      data: response,
      message: 'Tasa obtenida exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo tasa:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

// ==================== ESTADÍSTICAS Y REPORTES ====================

/**
 * Obtener estadísticas de pagos
 * @param {string} desde - Fecha desde
 * @param {string} hasta - Fecha hasta
 * @returns {Promise} Estadísticas
 */
export const obtenerEstadisticas = async (desde = null, hasta = null) => {
  try {
    let url = '/Pago/estadisticas';
    if (desde && hasta) {
      url += `?desde=${desde}&hasta=${hasta}`;
    }
    
    const response = await apiFetch(url);
    
    return {
      success: true,
      data: response,
      message: 'Estadísticas obtenidas exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo estadísticas:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener totales por período
 * @param {string} periodo - 'dia', 'semana', 'mes'
 * @param {number} dias - Número de días
 * @returns {Promise} Totales por período
 */
export const obtenerTotalesPorPeriodo = async (periodo = 'dia', dias = 30) => {
  try {
    const response = await apiFetch(`/Pago/totales?periodo=${periodo}&dias=${dias}`);
    
    return {
      success: true,
      data: response,
      message: 'Totales obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo totales:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener métodos de pago más usados
 * @param {string} desde - Fecha desde
 * @param {string} hasta - Fecha hasta
 * @returns {Promise} Métodos más usados
 */
export const obtenerMetodosMasUsados = async (desde = null, hasta = null) => {
  try {
    let url = '/Pago/metodos-mas-usados';
    if (desde && hasta) {
      url += `?desde=${desde}&hasta=${hasta}`;
    }
    
    const response = await apiFetch(url);
    
    return {
      success: true,
      data: response,
      message: 'Métodos obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo métodos más usados:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Validar si un pago puede ser anulado
 * @param {number} id - ID del pago
 * @returns {Promise} Resultado de la validación
 */
export const validarAnulacionPago = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de pago es requerido');
    }
    
    const response = await apiFetch(`/Pago/${id}/validar-anulacion`);
    
    return {
      success: true,
      data: response,
      message: 'Validación completada'
    };
    
  } catch (error) {
    console.error(`❌ Error validando anulación pago ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

// ==================== MÉTODOS DE PAGO ====================

/**
 * Obtener métodos de pago disponibles
 * @returns {Promise} Lista de métodos de pago
 */
export const obtenerMetodosPago = async () => {
  try {
    // Como no hay un endpoint específico, retornamos una lista estándar
    // En una implementación real, esto debería venir del backend
    return {
      success: true,
      data: [
        { idMetodoPago: 1, nombre: 'Efectivo', descripcion: 'Pago en efectivo' },
        { idMetodoPago: 2, nombre: 'Transferencia', descripcion: 'Transferencia bancaria' },
        { idMetodoPago: 3, nombre: 'Pago Móvil', descripcion: 'Pago móvil' },
        { idMetodoPago: 4, nombre: 'Tarjeta de Débito', descripcion: 'Pago con tarjeta de débito' },
        { idMetodoPago: 5, nombre: 'Tarjeta de Crédito', descripcion: 'Pago con tarjeta de crédito' },
        { idMetodoPago: 6, nombre: 'Zelle', descripcion: 'Transferencia Zelle' },
        { idMetodoPago: 7, nombre: 'PayPal', descripcion: 'Pago por PayPal' },
        { idMetodoPago: 8, nombre: 'Binance', descripcion: 'Pago en criptomonedas' }
      ],
      message: 'Métodos de pago obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo métodos de pago:', error);
    return {
      success: false,
      error: error.message,
      data: []
    };
  }
};

// ==================== FUNCIONES AUXILIARES PARA FILTRADO LOCAL ====================

/**
 * Filtrar pagos por estado localmente
 * @param {Array} pagos - Lista de pagos
 * @param {string} estado - Estado a filtrar
 * @returns {Array} Pagos filtrados
 */
export const filtrarPagosPorEstadoLocal = (pagos, estado) => {
  if (!estado || estado === 'todos') return pagos;
  return pagos.filter(p => p.estado?.toLowerCase() === estado.toLowerCase());
};

/**
 * Filtrar pagos por método localmente
 * @param {Array} pagos - Lista de pagos
 * @param {number} idMetodoPago - ID del método
 * @returns {Array} Pagos filtrados
 */
export const filtrarPagosPorMetodoLocal = (pagos, idMetodoPago) => {
  if (!idMetodoPago) return pagos;
  return pagos.filter(p => p.idMetodoPago === idMetodoPago);
};

/**
 * Buscar pagos localmente por texto
 * @param {Array} pagos - Lista de pagos
 * @param {string} termino - Término de búsqueda
 * @returns {Array} Pagos filtrados
 */
export const buscarPagosLocal = (pagos, termino) => {
  if (!termino || termino.trim() === '') return pagos;
  
  const terminoLower = termino.toLowerCase();
  return pagos.filter(p => 
    p.referencia?.toLowerCase().includes(terminoLower) ||
    p.descripcion?.toLowerCase().includes(terminoLower) ||
    p.metodo_pago?.toLowerCase().includes(terminoLower) ||
    p.usuario?.toLowerCase().includes(terminoLower) ||
    p.tipoPago?.toLowerCase().includes(terminoLower)
  );
};

/**
 * Ordenar pagos localmente
 * @param {Array} pagos - Lista de pagos
 * @param {string} criterio - Criterio de ordenamiento
 * @param {string} direccion - 'asc' o 'desc'
 * @returns {Array} Pagos ordenados
 */
export const ordenarPagosLocal = (pagos, criterio = 'fecha_pago', direccion = 'desc') => {
  const pagosCopy = [...pagos];
  
  const multiplicador = direccion.toLowerCase() === 'desc' ? -1 : 1;
  
  return pagosCopy.sort((a, b) => {
    switch (criterio) {
      case 'fecha_pago':
        return multiplicador * (new Date(b.fecha_pago || 0) - new Date(a.fecha_pago || 0));
      case 'monto_usd':
        return multiplicador * ((b.monto_usd || 0) - (a.monto_usd || 0));
      case 'referencia':
        return multiplicador * ((a.referencia || '').localeCompare(b.referencia || ''));
      case 'estado':
        return multiplicador * ((a.estado || '').localeCompare(b.estado || ''));
      default:
        return 0;
    }
  });
};

// ==================== EXPORT DEFAULT ====================

export default {
  // Funciones principales
  obtenerPagos,
  obtenerPagoPorId,
  crearPago,
  actualizarPago,
  anularPago,
  pagoExists,
  
  // Búsquedas
  buscarPagos,
  obtenerPagosPorVenta,
  obtenerPagosPorCuota,
  obtenerPagosPorRecepcion,
  obtenerPagosPorCliente,
  obtenerPagosPorUsuario,
  obtenerPagosPorMetodoPago,
  obtenerPagosPorEstado,
  obtenerPagosPorRangoFechas,
  
  // Conversiones
  convertirMoneda,
  obtenerTasaCambio,
  
  // Estadísticas
  obtenerEstadisticas,
  obtenerTotalesPorPeriodo,
  obtenerMetodosMasUsados,
  validarAnulacionPago,
  
  // Métodos de pago
  obtenerMetodosPago,
  
  // Utilidades de formateo
  formatearNumero,
  formatearPrecio,
  formatearFecha,
  convertirEstadoATexto,
  obtenerColorEstado,
  obtenerIconoEstado,
  obtenerIconoMetodoPago,
  
  // Filtros locales
  filtrarPagosPorEstadoLocal,
  filtrarPagosPorMetodoLocal,
  buscarPagosLocal,
  ordenarPagosLocal
};